/**
 * <video-progress-bar> — custom element that shows a thin progress line
 * at the bottom of a <deferred-media> video.
 *
 * Works with both looping and non-looping videos.
 * Uses requestAnimationFrame for buttery-smooth 60fps updates.
 */
class VideoProgressBar extends HTMLElement {
  /** @type {HTMLElement | null} */
  #fill = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #animationFrame = null;

  connectedCallback() {
    this.#fill = /** @type {HTMLElement | null} */ (this.querySelector('.video-progress-bar__fill'));
    if (!this.#fill) return;

    const media = this.closest('deferred-media');
    if (!media) return;

    // The video may already be in the DOM (autoplay) or loaded later (deferred).
    const video = media.querySelector('video');
    if (video) {
      this.#listen(video);
    } else {
      this.#waitForVideo(media);
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
    }
  }

  /**
   * Watch for the video element to appear (deferred-media loads it from a <template>).
   * @param {Element} media
   */
  #waitForVideo(media) {
    this.#observer = new MutationObserver(() => {
      const video = media.querySelector('video');
      if (video) {
        this.#observer?.disconnect();
        this.#listen(video);
      }
    });

    this.#observer.observe(media, { childList: true, subtree: true });
  }

  /**
   * Attach progress tracking to a video element.
   * @param {HTMLVideoElement} video
   */
  #listen(video) {
    const fill = this.#fill;
    if (!fill) return;

    // Use a rAF loop for smooth 60fps bar movement instead of the choppy ~4fps timeupdate.
    const tick = () => {
      if (video.duration > 0) {
        const percent = (video.currentTime / video.duration) * 100;
        fill.style.width = `${percent}%`;
      }
      this.#animationFrame = requestAnimationFrame(tick);
    };

    // Start the loop once the video begins playing.
    video.addEventListener('playing', () => {
      this.#animationFrame = requestAnimationFrame(tick);
    });

    // Stop the loop when paused to save resources.
    video.addEventListener('pause', () => {
      if (this.#animationFrame) {
        cancelAnimationFrame(this.#animationFrame);
        this.#animationFrame = null;
      }
    });

    // Pin bar at 100% when a non-looping video ends.
    video.addEventListener('ended', () => {
      fill.style.width = '100%';
    });

    // When looping, instantly reset the bar without a visible transition.
    video.addEventListener('seeked', () => {
      if (video.currentTime < 0.5) {
        fill.style.transition = 'none';
        fill.style.width = '0%';
        requestAnimationFrame(() => {
          fill.style.transition = '';
        });
      }
    });

    // If already playing (autoplay), kick off immediately.
    if (!video.paused) {
      this.#animationFrame = requestAnimationFrame(tick);
    }
  }
}

if (!customElements.get('video-progress-bar')) {
  customElements.define('video-progress-bar', VideoProgressBar);
}
